'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Link from 'next/link';
import { Trophy, Zap, Gift, Crown, Medal, Award, Users, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { fetchLeaderboard, subscribeToLeaderboard, LeaderboardEntry } from '@/lib/services/leaderboardService';
import { useAuth } from '@/lib/authContext';

const PRIZES = [
  {
    rank: 1,
    label: '🥇 Champion',
    prize: '₹10,000 Amazon Gift Card',
    badge: 'bg-gradient-to-br from-amber-400 to-yellow-300',
    ring: 'ring-4 ring-amber-300',
    textColor: 'text-amber-700',
    bgColor: 'bg-amber-50 border-amber-200',
    icon: <Crown size={20} className="text-amber-500" />,
  },
  {
    rank: 2,
    label: '🥈 Runner Up',
    prize: '₹5,000 Flipkart Voucher',
    badge: 'bg-gradient-to-br from-slate-400 to-slate-300',
    ring: 'ring-4 ring-slate-300',
    textColor: 'text-slate-600',
    bgColor: 'bg-slate-50 border-slate-200',
    icon: <Medal size={20} className="text-slate-500" />,
  },
  {
    rank: 3,
    label: '🥉 Third Place',
    prize: '₹2,500 Swiggy Credits',
    badge: 'bg-gradient-to-br from-orange-400 to-amber-300',
    ring: 'ring-4 ring-orange-300',
    textColor: 'text-orange-700',
    bgColor: 'bg-orange-50 border-orange-200',
    icon: <Award size={20} className="text-orange-500" />,
  },
];

const TIER_LABELS: Record<number, { label: string; color: string; bg: string }> = {
  1: { label: 'Legend', color: 'text-amber-700', bg: 'bg-amber-100' },
  2: { label: 'Expert', color: 'text-slate-600', bg: 'bg-slate-100' },
  3: { label: 'Pro', color: 'text-orange-700', bg: 'bg-orange-100' },
};

function getTier(rank: number) {
  if (rank <= 3) return TIER_LABELS[rank];
  if (rank <= 10) return { label: 'Elite', color: 'text-primary', bg: 'bg-primary/10' };
  if (rank <= 25) return { label: 'Rising', color: 'text-green-700', bg: 'bg-green-100' };
  return { label: 'Contributor', color: 'text-muted-foreground', bg: 'bg-muted' };
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) return <span className="text-2xl">🥇</span>;
  if (rank === 2) return <span className="text-2xl">🥈</span>;
  if (rank === 3) return <span className="text-2xl">🥉</span>;
  return (
    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
      <span className="text-sm font-extrabold text-muted-foreground">#{rank}</span>
    </div>
  );
}

function PointsBar({ points, maxPoints }: { points: number; maxPoints: number }) {
  const pct = maxPoints > 0 ? Math.min((points / maxPoints) * 100, 100) : 0;
  return (
    <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
      <div
        className="h-full rounded-full bg-gradient-to-r from-primary to-secondary transition-all duration-700"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export default function InfluencerLeaderboardPage() {
  const { user, profile } = useAuth();
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [myRank, setMyRank] = useState<LeaderboardEntry | null>(null);

  const loadLeaderboard = useCallback(async () => {
    setLoading(true);
    const data = await fetchLeaderboard(50);
    setLeaderboard(data);
    setLastUpdated(new Date());
    if (user) {
      const me = data.find((e) => e.userId === user.id) || null;
      setMyRank(me);
    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    loadLeaderboard();
    const unsub = subscribeToLeaderboard(() => {
      loadLeaderboard();
    });
    return unsub;
  }, [loadLeaderboard]);

  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);
  const maxPoints = leaderboard[0]?.totalPoints || 1;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">

        {/* Hero */}
        <section className="bg-secondary py-14 px-4 lg:px-8 xl:px-10 2xl:px-16 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-4 left-10 text-6xl">🏆</div>
            <div className="absolute top-8 right-20 text-5xl">⭐</div>
            <div className="absolute bottom-4 left-1/3 text-4xl">🎯</div>
          </div>
          <div className="max-w-screen-2xl mx-auto relative">
            <div className="text-center max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm text-slate-200 mb-4 border border-white/20">
                <Sparkles size={14} className="text-amber-400" />
                <span>Updated in real-time</span>
              </div>
              <h1 className="text-3xl lg:text-5xl font-extrabold text-white mb-3">
                Influencer Leaderboard
              </h1>
              <p className="text-slate-300 mb-6">
                Top reviewers win exciting prizes every month. Write reviews, earn points, and claim your spot!
              </p>
              <Link href="/write-review" className="inline-flex items-center gap-2 btn-primary px-6 py-3">
                <Zap size={16} /> Write a Review & Earn Points
              </Link>
            </div>
          </div>
        </section>

        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-10">

          {/* My Rank Banner */}
          {user && myRank && (
            <div className="mb-8 bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-extrabold text-lg">
                  {(profile?.full_name || user.email || 'U').charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-extrabold text-foreground">{profile?.full_name || 'You'}</p>
                  <p className="text-sm text-muted-foreground">Your current rank</p>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-primary">#{myRank.rank}</p>
                  <p className="text-xs text-muted-foreground">Rank</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-amber-600">{myRank.totalPoints}</p>
                  <p className="text-xs text-muted-foreground">Points</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-foreground">{myRank.reviewCount}</p>
                  <p className="text-xs text-muted-foreground">Reviews</p>
                </div>
              </div>
            </div>
          )}

          {user && !myRank && !loading && (
            <div className="mb-8 bg-muted rounded-2xl p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Zap size={20} className="text-amber-500" />
                <p className="text-sm font-semibold text-foreground">You haven't submitted any reviews yet. Start reviewing to earn points!</p>
              </div>
              <Link href="/write-review" className="btn-primary text-sm px-4 py-2 shrink-0">Write a Review</Link>
            </div>
          )}

          {/* Prize Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            {PRIZES.map((prize) => {
              const winner = leaderboard.find((e) => e.rank === prize.rank);
              return (
                <div key={prize.rank} className={`rounded-2xl border p-5 ${prize.bgColor}`}>
                  <div className="flex items-center gap-2 mb-3">
                    {prize.icon}
                    <span className={`font-extrabold text-sm ${prize.textColor}`}>{prize.label}</span>
                  </div>
                  <p className={`font-extrabold text-lg mb-1 ${prize.textColor}`}>{prize.prize}</p>
                  {winner ? (
                    <div className="flex items-center gap-2 mt-3">
                      <div className={`w-8 h-8 rounded-full ${prize.badge} flex items-center justify-center text-white font-bold text-xs ${prize.ring}`}>
                        {winner.fullName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-foreground">{winner.fullName}</p>
                        <p className="text-xs text-muted-foreground">{winner.totalPoints} pts</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-3 italic">No entries yet — be the first!</p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Leaderboard Table */}
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Trophy size={18} className="text-amber-500" />
                <h2 className="font-extrabold text-foreground">Full Rankings</h2>
                {leaderboard.length > 0 && (
                  <span className="bg-muted text-muted-foreground text-xs font-semibold px-2 py-0.5 rounded-full">
                    {leaderboard.length} reviewers
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {lastUpdated && (
                  <span className="text-xs text-muted-foreground hidden sm:block">
                    Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
                <button
                  onClick={loadLeaderboard}
                  disabled={loading}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
                  title="Refresh"
                >
                  <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20 gap-3 text-muted-foreground">
                <Loader2 size={20} className="animate-spin" />
                <span className="text-sm font-semibold">Loading leaderboard...</span>
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-20">
                <Trophy size={40} className="text-muted-foreground mx-auto mb-4 opacity-30" />
                <p className="font-bold text-foreground mb-2">No rankings yet</p>
                <p className="text-sm text-muted-foreground mb-4">Be the first to write a review and claim the top spot!</p>
                <Link href="/write-review" className="btn-primary px-6 py-2.5 inline-flex items-center gap-2">
                  <Zap size={14} /> Write First Review
                </Link>
              </div>
            ) : (
              <div>
                {/* Top 3 podium */}
                {top3.length > 0 && (
                  <div className="grid grid-cols-3 gap-0 border-b border-border">
                    {top3.map((entry) => {
                      const prize = PRIZES.find((p) => p.rank === entry.rank);
                      const isMe = user?.id === entry.userId;
                      return (
                        <div
                          key={entry.userId}
                          className={`flex flex-col items-center py-6 px-3 text-center border-r border-border last:border-r-0 ${isMe ? 'bg-primary/5' : ''}`}
                        >
                          <RankBadge rank={entry.rank} />
                          <div className={`w-14 h-14 rounded-full mt-3 mb-2 flex items-center justify-center text-white font-extrabold text-xl ${prize?.badge || 'bg-muted'}`}>
                            {entry.fullName.charAt(0).toUpperCase()}
                          </div>
                          <p className="font-extrabold text-sm text-foreground truncate max-w-full px-1">
                            {entry.fullName}{isMe ? ' (You)' : ''}
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            <Zap size={12} className="text-amber-500" />
                            <span className="text-sm font-extrabold text-amber-600">{entry.totalPoints} pts</span>
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span>{entry.reviewCount} reviews</span>
                            <span>{entry.totalHelpful} helpful</span>
                          </div>
                          {prize && (
                            <span className={`mt-2 text-xs font-semibold px-2 py-0.5 rounded-full ${prize.textColor} ${prize.bgColor} border`}>
                              {prize.prize}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Rest of leaderboard */}
                {rest.length > 0 && (
                  <div className="divide-y divide-border">
                    {rest.map((entry) => {
                      const tier = getTier(entry.rank);
                      const isMe = user?.id === entry.userId;
                      return (
                        <div
                          key={entry.userId}
                          className={`flex items-center gap-4 px-6 py-4 hover:bg-muted/50 transition-colors ${isMe ? 'bg-primary/5' : ''}`}
                        >
                          <RankBadge rank={entry.rank} />
                          <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-white font-bold text-sm shrink-0">
                            {entry.fullName.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-bold text-sm text-foreground truncate">
                                {entry.fullName}{isMe ? ' (You)' : ''}
                              </p>
                              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${tier.bg} ${tier.color}`}>
                                {tier.label}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <PointsBar points={entry.totalPoints} maxPoints={maxPoints} />
                              <span className="text-xs text-muted-foreground shrink-0">{entry.reviewCount} reviews</span>
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="flex items-center gap-1 justify-end">
                              <Zap size={12} className="text-amber-500" />
                              <span className="font-extrabold text-amber-600">{entry.totalPoints}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">{entry.totalHelpful} helpful</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CTA */}
          <div className="mt-10 bg-gradient-to-r from-secondary to-primary rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-extrabold text-white mb-2">Ready to Climb the Ranks?</h3>
            <p className="text-slate-300 mb-6">Share your college experience and help thousands of students make better decisions.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/write-review" className="inline-flex items-center gap-2 bg-white text-secondary font-extrabold px-6 py-3 rounded-xl hover:bg-slate-100 transition-colors">
                <Zap size={16} /> Write a Review
              </Link>
              <Link href="/" className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white font-semibold px-6 py-3 rounded-xl hover:bg-white/20 transition-colors">
                <Users size={16} /> Browse Colleges
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
